// 📁 contexts/MenoresContext.js
import React, { createContext, useContext, useState, useCallback, useRef } from 'react';

const MenoresContext = createContext();

export const useMenores = () => {
  const context = useContext(MenoresContext);
  if (!context) {
    throw new Error('useMenores must be used within a MenoresProvider');
  }
  return context;
};

export const MenoresProvider = ({ children }) => {
  const [isLoaded, setIsLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [lastLoadTime, setLastLoadTime] = useState(null);
  const [hasStartedLoading, setHasStartedLoading] = useState(false);
  
  // ✅ USAR useRef para prevenir múltiples cargas
  const iframeRef = useRef(null);
  const loadingStartedRef = useRef(false);

  // ✅ FUNCIÓN para marcar como cargado
  const handleIframeLoad = useCallback(() => {
    console.log('📊 Iframe Menores cargado completamente');
    setIsLoading(false);
    setIsLoaded(true);
    setLastLoadTime(new Date());
    loadingStartedRef.current = false; // Reset para permitir futuras recargas manuales
  }, []);

  // ✅ FUNCIÓN para iniciar carga (solo si no está cargado Y no se ha iniciado ya)
  const startLoading = useCallback(() => {
    // ✅ VERIFICACIONES MÚLTIPLES para prevenir carga duplicada
    if (isLoaded) {
      console.log('⚡ Menores ya está cargado - saltando carga');
      return false;
    }
    
    if (isLoading || loadingStartedRef.current) {
      console.log('🔄 Menores ya está en proceso de carga - saltando');
      return false;
    }

    if (hasStartedLoading && !isLoaded) {
      console.log('⏳ Menores ya intentó cargar anteriormente - esperando...');
      return false;
    }

    console.log('🚀 Iniciando carga de Menores por primera vez...');
    setIsLoading(true);
    setHasStartedLoading(true);
    loadingStartedRef.current = true;
    return true;
  }, [isLoaded, isLoading, hasStartedLoading]);

  // ✅ FUNCIÓN para forzar recarga
  const forceReload = useCallback(() => {
    console.log('🔄 Forzando recarga manual de Menores...');
    setIsLoaded(false);
    setIsLoading(true);
    setHasStartedLoading(true);
    setLastLoadTime(null);
    loadingStartedRef.current = true;
    
    // ✅ FORZAR recarga del iframe agregando timestamp
    if (iframeRef.current) {
      const currentSrc = iframeRef.current.src;
      const separator = currentSrc.includes('?') ? '&' : '?';
      iframeRef.current.src = `${currentSrc}${separator}_reload=${Date.now()}`;
    }
  }, []);

  // ✅ FUNCIÓN para verificar si debería mostrar el iframe
  const shouldShowIframe = useCallback(() => {
    return isLoaded || isLoading;
  }, [isLoaded, isLoading]);

  // ✅ FUNCIÓN para verificar si debería mostrar el spinner
  const shouldShowSpinner = useCallback(() => {
    return isLoading && !isLoaded;
  }, [isLoading, isLoaded]);

  // ✅ FUNCIÓN para limpiar estado (útil para logout)
  const resetState = useCallback(() => {
    console.log('🗑️ Limpiando estado de Menores...');
    setIsLoaded(false);
    setIsLoading(false);
    setHasStartedLoading(false);
    setLastLoadTime(null);
    loadingStartedRef.current = false;
  }, []);

  const value = {
    isLoaded,
    isLoading,
    lastLoadTime,
    hasStartedLoading,
    handleIframeLoad,
    startLoading,
    forceReload,
    shouldShowIframe,
    shouldShowSpinner,
    resetState,
    iframeRef
  };

  return (
    <MenoresContext.Provider value={value}>
      {children}
    </MenoresContext.Provider>
  );
};